<script setup lang="ts">
import type { TestHortProCredentialsResponse, TestHortProFehler } from '@/services/nutzer.service'

interface HortProForm {
    username: string
    password: string
}

const form = ref<HortProForm>({ username: '', password: '' })
const authStore = useAuthStore()
const nutzerStore = useNutzerStore()

const isValid = ref(false)
const showPassword = ref(false)
const editing = ref(false)
const message = ref<string | null>(null)
const testResult = ref<TestHortProCredentialsResponse | null>(null)

const rules = {
    required: (v: string) => !!v || 'Pflichtfeld',
}

const submitting = ref(false)
const isTesting = computed(() => nutzerStore.isMethodLoading('testHortProCredentials'))
const nutzer = computed(() => nutzerStore.getNutzerById((authStore.currentUser as any)?.id))
const hasCredentials = computed(() => !!(nutzer.value as any)?.hasHortProCredentials)

const fehlertexte: Record<TestHortProFehler, string> = {
    credentials_invalid: 'Benutzername oder Passwort stimmen nicht.',
    unreachable: 'HortPro ist gerade nicht erreichbar. Bitte später erneut versuchen.',
    nutzer_ohne_credentials: 'Es sind noch keine HortPro-Zugangsdaten hinterlegt.',
}

const testErfolgText = computed(() => {
    if (!testResult.value || !testResult.value.verbunden) return null
    const namen = testResult.value.kindernamen
    if (namen.length === 0) {
        return 'Verbindung erfolgreich – aber kein Kind im Account gefunden.'
    }
    return `Verbindung erfolgreich – gefunden: ${namen.join(', ')}.`
})

const testFehlerText = computed(() => {
    if (!testResult.value || testResult.value.verbunden) return null
    const fehler = testResult.value.fehler
    if (!fehler) return 'Verbindung fehlgeschlagen.'
    return fehlertexte[fehler] ?? 'Verbindung fehlgeschlagen.'
})

onMounted(() => {
    if (authStore.currentUser) {
        nutzerStore.fetchNutzerById((authStore.currentUser as any).id)
    }
})

function clearStatus() {
    message.value = null
    testResult.value = null
}

async function onSubmit() {
    if (!isValid.value || !authStore.currentUser) return
    clearStatus()

    submitting.value = true
    try {
        // Schritt 1: Zugangsdaten gegen HortPro pruefen, bevor wir sie speichern.
        let test: TestHortProCredentialsResponse
        try {
            test = await nutzerStore.testHortProCredentials({
                hortProUsername: form.value.username,
                hortProPassword: form.value.password,
            })
        } catch (e) {
            console.error(e)
            testResult.value = { verbunden: false, kindernamen: [], fehler: 'unreachable' }
            return
        }

        if (!test.verbunden) {
            testResult.value = test
            return
        }

        // Schritt 2: Erst nach erfolgreichem Test wirklich speichern.
        try {
            await nutzerStore.saveHortProCredentials({
                hortProUsername: form.value.username,
                hortProPassword: form.value.password,
                nutzer: authStore.currentUser['@id'],
            })
            await nutzerStore.fetchNutzerById((authStore.currentUser as any).id)

            const namen = test.kindernamen
            message.value = namen.length > 0
                ? `Zugangsdaten gespeichert – verbunden mit: ${namen.join(', ')}.`
                : 'Zugangsdaten gespeichert.'
            editing.value = false
        } catch (e) {
            console.error(e)
            message.value = 'Fehler beim Speichern der Zugangsdaten.'
        }
    } finally {
        submitting.value = false
    }
}

async function testGespeicherteCredentials() {
    if (!authStore.currentUser) return
    clearStatus()

    try {
        testResult.value = await nutzerStore.testHortProCredentials({
            nutzer: (authStore.currentUser as any)['@id'],
        })
    } catch (e) {
        console.error(e)
        testResult.value = { verbunden: false, kindernamen: [], fehler: 'unreachable' }
    }
}

function enableEdit() {
    editing.value = true
    clearStatus()
    form.value.username = ''
    form.value.password = ''
}
</script>

<template>
    <v-card class="mx-auto" elevation="2">
        <v-card-title class="text-h6 font-semibold">HortPro Zugang</v-card-title>

        <v-card-subtitle class="text-body-2 mb-4">
            Verknüpfe deinen HortPro-Account, um Daten automatisch zu synchronisieren.
        </v-card-subtitle>

        <v-divider class="mb-4" />

        <v-card-text>
            <!-- ✅ Wenn Zugangsdaten bereits existieren -->
            <v-slide-y-transition>
                <div v-if="hasCredentials && !editing">
                    <v-alert
                        type="success"
                        variant="tonal"
                        title="Zugangsdaten hinterlegt"
                        text="Dein HortPro-Account ist verbunden."
                    />
                </div>
            </v-slide-y-transition>

            <!-- 📝 Formular (wenn keine Daten oder Bearbeitung aktiv) -->
            <v-slide-y-transition>
                <v-form
                    v-if="!hasCredentials || editing"
                    v-model="isValid"
                    @submit.prevent="onSubmit"
                >
                    <input type="text" name="username" style="display:none">
                    <input type="password" name="password" style="display:none">
                    <v-text-field
                        v-model="form.username"
                        label="HortPro Benutzername"
                        prepend-icon="mdi-account"
                        :rules="[rules.required]"
                        clearable
                        variant="outlined"
                        density="comfortable"
                        autocomplete="hortpro-1"
                        name="hortpro-1"
                    />

                    <v-text-field
                        v-model="form.password"
                        label="HortPro Passwort"
                        prepend-icon="mdi-lock"
                        :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                        :type="showPassword ? 'text' : 'password'"
                        @click:append-inner="showPassword = !showPassword"
                        :rules="[rules.required]"
                        clearable
                        variant="outlined"
                        density="comfortable"
                        autocomplete="hortpro-2"
                        name="hortpro-2"
                    />

                    <div class="d-flex flex-wrap justify-end mt-4 ga-2">
                        <v-btn
                            color="primary"
                            type="button"
                            variant="text"
                            :disabled="submitting"
                            @click="editing = false"
                        >
                            Nichts ändern
                        </v-btn>
                        <v-btn
                            color="primary"
                            type="submit"
                            :loading="submitting"
                            :disabled="!isValid"
                        >
                            Speichern
                        </v-btn>
                    </div>
                    <p class="text-caption text-medium-emphasis mt-2 mb-0 text-right">
                        Beim Speichern wird automatisch geprüft, ob die Zugangsdaten bei HortPro funktionieren.
                    </p>
                </v-form>
            </v-slide-y-transition>

            <!-- 🔌 Test-Ergebnis -->
            <v-slide-y-transition>
                <v-alert
                    v-if="testResult"
                    class="mt-4"
                    :type="testResult.verbunden ? 'success' : 'error'"
                    variant="tonal"
                    :icon="testResult.verbunden ? 'mdi-check-circle' : 'mdi-alert-circle'"
                    closable
                    @click:close="testResult = null"
                >
                    <template v-if="testResult.verbunden">
                        <div class="font-weight-medium">{{ testErfolgText }}</div>
                    </template>
                    <template v-else>
                        <div class="font-weight-medium">{{ testFehlerText }}</div>
                    </template>
                </v-alert>
            </v-slide-y-transition>

            <!-- ℹ️ Speicher-Feedback -->
            <v-slide-y-transition>
                <v-alert
                    v-if="message"
                    class="mt-4"
                    :type="message.includes('Fehler') || message.includes('fehlgeschlagen') ? 'error' : 'success'"
                    variant="tonal"
                    closable
                    @click:close="message = null"
                >
                    {{ message }}
                </v-alert>
            </v-slide-y-transition>
        </v-card-text>

        <!-- 🔘 Aktionen unten -->
        <v-slide-y-transition>
            <v-card-actions
                v-if="hasCredentials && !editing"
                class="flex-wrap px-4 pb-4 pt-0 ga-2"
            >
                <v-btn
                    color="primary"
                    variant="flat"
                    :loading="isTesting"
                    prepend-icon="mdi-lan-connect"
                    @click="testGespeicherteCredentials"
                >
                    Verbindung jetzt testen
                </v-btn>

                <v-btn
                    color="secondary"
                    variant="tonal"
                    :disabled="isTesting"
                    @click="enableEdit"
                >
                    Zugangsdaten ändern
                </v-btn>
            </v-card-actions>
        </v-slide-y-transition>
    </v-card>
</template>
